import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Home,
  FolderKanban,
  MessageSquare,
  Inbox,
  LayoutDashboard,
  Images,
  Loader2,
} from "lucide-react";
import AdminGuard from "@/components/admin/AdminGuard";
import DashboardPanel from "@/components/admin/DashboardPanel";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import ProgettiPanel from "@/components/admin/ProgettiPanel";
import TestimonianzePanel from "@/components/admin/TestimonianzePanel";
import MediaPanel from "@/components/admin/MediaPanel";
import { logout } from "@/lib/api";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, Panel: DashboardPanel },
  { key: "richieste", label: "Richieste", icon: Inbox, Panel: SubmissionsPanel },
  { key: "progetti", label: "Progetti", icon: FolderKanban, Panel: ProgettiPanel },
  { key: "testimonianze", label: "Testimonianze", icon: MessageSquare, Panel: TestimonianzePanel },
  { key: "media", label: "Media", icon: Images, Panel: MediaPanel },
];

export default function AdminDashboard() {
  return <AdminGuard>{(user) => <Dashboard user={user} />}</AdminGuard>;
}

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [tab, setTab] = useState("dashboard");

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      // ignora — sessione probabilmente già scaduta
    }
    navigate("/admin/login", { replace: true });
  };

  const ActivePanel = TABS.find((t) => t.key === tab)?.Panel || DashboardPanel;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F8FAFC] lg:flex">
      {/* Sidebar (barra in alto su mobile) */}
      <aside className="bg-[#0B1E36] text-white lg:w-60 lg:flex-shrink-0 lg:h-screen lg:sticky lg:top-0 flex flex-col sticky top-0 z-40">
        <div className="px-5 lg:px-6 pt-5 lg:pt-7 pb-4 flex items-center justify-between lg:block">
          <div>
            <div className="text-lg font-bold tracking-tight">Finestre e Colore</div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#5AC8FA] mt-1">
              Area riservata
            </div>
          </div>
          <div className="flex items-center gap-1 lg:hidden">
            <button
              onClick={() => navigate("/")}
              className="p-2 text-white/70 hover:text-white"
              aria-label="Vedi sito"
            >
              <Home className="w-[18px] h-[18px]" />
            </button>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="p-2 text-white/70 hover:text-[#FF3B30] disabled:opacity-50"
              aria-label="Esci"
            >
              {loggingOut ? (
                <Loader2 className="w-[18px] h-[18px] animate-spin" />
              ) : (
                <LogOut className="w-[18px] h-[18px]" />
              )}
            </button>
          </div>
        </div>

        <nav
          className="flex lg:flex-col gap-1 px-3 pb-3 lg:pb-0 overflow-x-auto lg:overflow-visible lg:flex-1"
          role="tablist"
          aria-label="Sezioni admin"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap lg:w-full ${
                  active
                    ? "bg-white text-[#0B1E36]"
                    : "text-white/70 hover:text-white hover:bg-white/10"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:block px-3 py-4 border-t border-white/10 space-y-1">
          {user?.email && (
            <div className="px-3 pb-2 text-xs text-white/50 truncate" title={user.email}>
              {user.email}
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="w-full inline-flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="admin-view-site"
          >
            <Home className="w-4 h-4" />
            Vedi sito
          </button>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="w-full inline-flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md text-white/70 hover:text-white hover:bg-[#FF3B30] transition-colors disabled:opacity-50"
            data-testid="admin-logout"
          >
            {loggingOut ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            Esci
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-5 sm:px-8 lg:px-10 py-8 md:py-12">
        <div className="max-w-[1240px] mx-auto">
          <ActivePanel onNavigate={setTab} />
        </div>
      </main>
    </div>
  );
}
